import {
  alpha,
  Box,
  Button,
  Chip,
  IconButton,
  Link,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useMemo } from "react";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DoneIcon from "@mui/icons-material/Done";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid-premium";
import { HotkeyCompliantDataGrid } from "src/components/common/dataGrid";

import {
  DeploymentHistory,
  SessionSummary,
  useListSessionVersionsQuery,
} from "store/api/generatedApi";
import { useNavigateToSearch } from "hooks/navigation";
import { useFormatLocalDateString } from "hooks/useFormatLocalDate";
import { useTenantTranslation } from "hooks/formatters";
import { useTenant } from "hooks/settings";
import { useDebouncedValue } from "hooks/useDebouncedValue";
import { useNumberSerialiser } from "hooks/serialisers/numbers";
import { SearchStatusChip } from "../search/common/SearchStatus";
import { useFormatVersionLabel } from "../search/common/versionLabel";
import { SessionOptionsButton } from "./SessionOptionsButton";
import { useCanDeploy } from "hooks/useCanDeploy";
import { defaultPlanName } from "../plan/name";
import { useSyncedResultsState } from "contexts/search/results";
import { sorted } from "helpers";
import { UserLabel } from "src/components/common/userLabel/UserLabel";
import {
  containsText,
  HighlightedText,
} from "src/components/common/HighlightedText";
import { TreeLine } from "src/components/common/TreeLine";
import { userDisplayName } from "src/utils/userDisplay";
import {
  listSearchesGrid,
  openSessionsReducer,
  SearchesGridRow,
  SessionMatcher,
  versionsBySessionId,
} from "./sessionVersions";

interface Props {
  sessions: SessionSummary[];
}

const TYPING_DELAY_MILLISECONDS = 200;
const CHEVRON_WIDTH = 24;
const TRUNCATED_TEXT_SX = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  textAlign: "left",
} as const;

const firstDeployment = (deployments: DeploymentHistory[]) =>
  sorted(deployments, (item) => new Date(item.deployed_at).getTime())[0];

const versionDeployments = (session: SessionSummary, searchId: number) =>
  session.deployment_history.filter(({ search_id }) => search_id === searchId);

const rowDeployments = (row: SearchesGridRow): DeploymentHistory[] =>
  row.kind === "session"
    ? row.session.deployment_history
    : versionDeployments(row.session, row.version.search_id);

export const SearchesTable = ({ sessions }: Props) => {
  const { t } = useTenantTranslation();
  const tenantName = useTenant();
  const formatLocalDate = useFormatLocalDateString("datetime");
  const { format: formatCount } = useNumberSerialiser();
  const canDeploy = useCanDeploy();
  const formatVersionLabel = useFormatVersionLabel();
  const [typedText, setTypedText] = React.useState("");
  const debouncedText = useDebouncedValue(
    typedText.trim(),
    TYPING_DELAY_MILLISECONDS
  );
  const highlight = debouncedText === "" ? undefined : debouncedText;
  const [openSessions, dispatchOpenSessions] = React.useReducer(
    openSessionsReducer,
    { base: "matching", toggled: new Set<number>() }
  );
  const [openedFor, setOpenedFor] = React.useState(highlight);
  if (openedFor !== highlight) {
    setOpenedFor(highlight);
    dispatchOpenSessions({ type: "openMatching" });
  }

  const { data: versionsResponse } = useListSessionVersionsQuery(
    { tenantName },
    { refetchOnMountOrArgChange: true }
  );
  const versionsBySession = useMemo(
    () => versionsBySessionId(versionsResponse),
    [versionsResponse]
  );

  const matcher = useMemo((): SessionMatcher | undefined => {
    if (highlight === undefined) {
      return undefined;
    }
    const deploymentTexts = (deployments: DeploymentHistory[]) =>
      canDeploy
        ? deployments.map(({ deployed_at }) => formatLocalDate(deployed_at))
        : [];
    const matchesAny = (texts: string[]) =>
      texts.some((text) => containsText(text, highlight));
    return {
      matchesName: (session) => containsText(session.name, highlight),
      matchesSessionRow: (session) => {
        const deployment = firstDeployment(session.deployment_history);
        return matchesAny([
          session.name,
          userDisplayName(session.user),
          formatLocalDate(session.created_at),
          ...deploymentTexts(deployment === undefined ? [] : [deployment]),
        ]);
      },
      matchesVersion: (session, version) =>
        matchesAny([
          formatVersionLabel(version.version_number),
          userDisplayName(version.user),
          formatLocalDate(version.created_at),
          t(version.status),
          ...deploymentTexts(versionDeployments(session, version.search_id)),
        ]),
    };
  }, [highlight, canDeploy, formatLocalDate, formatVersionLabel, t]);

  const grid = useMemo(
    () =>
      listSearchesGrid({ sessions, versionsBySession, openSessions, matcher }),
    [sessions, versionsBySession, openSessions, matcher]
  );

  const columns = React.useMemo((): GridColDef<SearchesGridRow>[] => {
    const baseColumns: GridColDef<SearchesGridRow>[] = [
      {
        field: "name",
        headerName: t("name"),
        flex: 2,
        display: "flex",
        renderCell: ({
          row,
          tabIndex,
        }: GridRenderCellParams<SearchesGridRow>) => (
          <SessionNameCell
            row={row}
            highlight={highlight}
            tabIndex={tabIndex}
            onToggle={(sessionId) => {
              dispatchOpenSessions({ type: "toggle", sessionId });
            }}
          />
        ),
      },
      {
        field: "created_at",
        headerName: t("createdAtHeader"),
        flex: 1,
        display: "flex",
        renderCell: ({ row }: GridRenderCellParams<SearchesGridRow>) => (
          <Typography variant="inherit">
            <HighlightedText
              text={formatLocalDate(
                row.kind === "session"
                  ? row.session.created_at
                  : row.version.created_at
              )}
              query={highlight}
            />
          </Typography>
        ),
      },
      {
        field: "user",
        headerName: t("createdByHeader"),
        display: "flex",
        flex: 1,
        renderCell: ({ row }: GridRenderCellParams<SearchesGridRow>) => (
          <UserLabel
            user={row.kind === "session" ? row.session.user : row.version.user}
            variant="body1"
            highlight={highlight}
          />
        ),
      },
      {
        field: "history",
        cellClassName: "history",
        headerName: t("history"),
        display: "flex",
        flex: 1,
        renderCell: ({ row }: GridRenderCellParams<SearchesGridRow>) =>
          row.kind === "session" ? (
            <HistoryCell session={row.session} />
          ) : (
            <Box sx={{ display: "flex", paddingY: 0.5, paddingX: 1 }}>
              <SearchStatusChip searchStatus={row.version.status} />
            </Box>
          ),
      },
      {
        field: "options",
        headerName: "",
        width: 45,
        renderCell: SessionOptionsButtonCell,
      },
    ];
    if (canDeploy) {
      const deploymentColumn: GridColDef<SearchesGridRow> = {
        field: "deployment",
        headerName: t("deployedHeader"),
        flex: 1,
        renderCell: ({ row }: GridRenderCellParams<SearchesGridRow>) => (
          <DeployedCell
            deployments={rowDeployments(row)}
            highlight={highlight}
          />
        ),
      };
      return [
        ...baseColumns.slice(0, 1),
        deploymentColumn,
        ...baseColumns.slice(1),
      ];
    } else {
      return baseColumns;
    }
  }, [canDeploy, t, formatLocalDate, highlight]);

  const searchCount = formatCount(grid.sessionCount);
  const countLabel =
    highlight !== undefined
      ? t("matchingSearchSessionCount", { searches: searchCount })
      : versionsBySession.size === 0
        ? t("searchCount", { searches: searchCount })
        : t("searchSessionCount", {
            searches: searchCount,
            versions: formatCount(grid.versionCount),
          });

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1.5,
          mb: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", minWidth: 200 }}
          >
            {countLabel}
          </Typography>
          <Button
            variant="outlined"
            size="small"
            disabled={!grid.canOpenAnySession}
            onClick={() => {
              dispatchOpenSessions({ type: "expandAll" });
            }}
          >
            {t("expandAll")}
          </Button>
          <Button
            variant="outlined"
            size="small"
            disabled={!grid.canOpenAnySession}
            onClick={() => {
              dispatchOpenSessions({ type: "collapseAll" });
            }}
          >
            {t("collapseAll")}
          </Button>
        </Box>
        <TextField
          size="small"
          type="search"
          value={typedText}
          onChange={(event) => {
            setTypedText(event.target.value);
          }}
          placeholder={t("findSearches")}
          sx={{ width: 320, maxWidth: "100%" }}
        />
      </Box>
      <HotkeyCompliantDataGrid<SearchesGridRow>
        rows={grid.rows}
        columns={columns}
        getRowId={(row) => row.id}
        getRowHeight={() => "auto"}
        getRowClassName={({ row }) =>
          row.kind === "version" ? "session-version" : ""
        }
        localeText={
          highlight === undefined
            ? {}
            : { noRowsLabel: t("nothingMatches", { query: highlight }) }
        }
        sx={{
          flex: 1,
          minHeight: 200,
          ".history": {
            padding: 0,
          },
          "& .session-version": {
            backgroundColor: ({ palette }) => alpha(palette.grey[50], 0.5),
          },
        }}
        hideFooter
        disableRowSelectionOnClick
        disableColumnFilter
        disableColumnSorting
        disableRowGrouping
        disableAggregation
      />
    </Box>
  );
};

const DeployedCellTooltip = ({
  planId,
  username,
  deployedAt,
  email,
  searchId,
}: {
  planId: number;
  username: string;
  deployedAt: string;
  email: string;
  searchId: number;
  sessionId: number;
}) => {
  const { results } = useSyncedResultsState(searchId, 0, false, () => null);
  const theme = useTheme();
  const { t } = useTenantTranslation();

  const children = (() => {
    if (results.status === "success") {
      const planName = defaultPlanName(planId);
      return (
        <>
          {t("userDeployedPlanAt", { username, email, planName, deployedAt })}
        </>
      );
    } else {
      return (
        <>
          <Skeleton
            sx={{
              bgcolor: theme.palette.common.white,
              width: 200,
              padding: 0,
            }}
          />
          <Skeleton
            sx={{
              bgcolor: theme.palette.common.white,
              width: 50,
              padding: 0,
            }}
          />
          <Skeleton
            sx={{
              bgcolor: theme.palette.common.white,
              width: 150,
              padding: 0,
              display: "inline-block",
            }}
          />
          <Skeleton
            sx={{
              bgcolor: theme.palette.common.white,
              width: 100,
              padding: 0,
            }}
          />
        </>
      );
    }
  })();

  return children;
};

const DeployedCell = ({
  deployments,
  highlight,
}: {
  deployments: DeploymentHistory[];
  highlight: string | undefined;
}) => {
  const theme = useTheme();
  const formatLocalDate = useFormatLocalDateString("datetime");

  if (deployments.length > 0) {
    const lastDeployed = firstDeployment(deployments);

    if (lastDeployed) {
      return (
        <Box
          alignItems="center"
          gap={0.5}
          sx={{
            display: "flex",
            py: 1,
            width: "100%",
            overflow: "hidden",
          }}
        >
          <DoneIcon sx={{ fill: theme.palette.success.main }} />
          <Typography
            variant="body1"
            sx={{
              overflow: "hidden",
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
              wordBreak: "unset",
            }}
          >
            <HighlightedText
              text={formatLocalDate(lastDeployed.deployed_at)}
              query={highlight}
            />
          </Typography>
          <Tooltip
            title={
              <DeployedCellTooltip
                searchId={lastDeployed.search_id}
                sessionId={lastDeployed.session_id}
                planId={lastDeployed.plan_id}
                username={lastDeployed.username}
                deployedAt={formatLocalDate(lastDeployed.deployed_at)}
                email={lastDeployed.email}
              />
            }
          >
            <InfoRoundedIcon sx={{ marginLeft: "auto" }} />
          </Tooltip>
        </Box>
      );
    } else {
      return null;
    }
  } else {
    return null;
  }
};

const HistoryCell = ({ session }: { session: SessionSummary }) => {
  const { searches_failed, searches_succeeded, searches_running } = session;

  const chip = useMemo(() => {
    if (
      searches_failed === 0 &&
      searches_succeeded === 0 &&
      searches_running === 0
    ) {
      return <SearchStatusChip searchStatus="not_started" />;
    } else if (searches_failed === 0 && searches_succeeded === 0) {
      return <SearchStatusChip searchStatus="running" />;
    } else {
      return (
        <>
          {searches_succeeded > 0 && (
            <SearchStatusChip
              searchStatus="success"
              count={searches_succeeded}
            />
          )}
          {searches_failed > 0 && (
            <SearchStatusChip searchStatus="failure" count={searches_failed} />
          )}
          {searches_running > 0 && (
            <SearchStatusChip searchStatus="running" count={searches_running} />
          )}
        </>
      );
    }
  }, [searches_failed, searches_running, searches_succeeded]);

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        width: "100%",
        paddingY: 0.5,
        paddingX: 1,
        columnGap: 1,
        rowGap: 0.5,
      }}
    >
      {chip}
    </Box>
  );
};

const SessionNameCell = ({
  row,
  highlight,
  tabIndex,
  onToggle,
}: {
  row: SearchesGridRow;
  highlight: string | undefined;
  tabIndex: 0 | -1;
  onToggle: (sessionId: number) => void;
}) => {
  const { t } = useTenantTranslation();
  const formatVersionLabel = useFormatVersionLabel();
  const navigate = useNavigateToSearch();
  const cellSx = {
    display: "flex",
    alignItems: "center",
    alignSelf: "stretch",
    width: "100%",
    minWidth: 0,
    gap: 0.5,
  } as const;

  if (row.kind === "version") {
    return (
      <Box sx={cellSx}>
        <TreeLine
          isLast={row.isLastVersion}
          left={CHEVRON_WIDTH / 2 - 1}
          width={CHEVRON_WIDTH}
        />
        <Tooltip title={t("openSearch")}>
          <Link
            component="button"
            variant="body1"
            underline="hover"
            tabIndex={tabIndex}
            onClick={() => {
              navigate(row.version.search_id);
            }}
            sx={TRUNCATED_TEXT_SX}
          >
            <HighlightedText
              text={formatVersionLabel(row.version.version_number)}
              query={highlight}
            />
          </Link>
        </Tooltip>
      </Box>
    );
  }

  const { session, versionCount, listedVersionCount, isOpen } = row;
  const canOpen = versionCount > 1;
  const toggleLabel = t(isOpen ? "hideVersions" : "showVersions");

  return (
    <Box sx={cellSx}>
      {canOpen ? (
        <Tooltip title={toggleLabel}>
          <IconButton
            size="small"
            tabIndex={tabIndex}
            aria-expanded={isOpen}
            aria-label={toggleLabel}
            onClick={() => {
              onToggle(session.session_id);
            }}
            sx={{ p: 0.25, flex: "none" }}
          >
            <ChevronRightIcon
              fontSize="small"
              sx={{
                transition: "transform 120ms",
                transform: isOpen ? "rotate(90deg)" : "none",
              }}
            />
          </IconButton>
        </Tooltip>
      ) : (
        <Box sx={{ width: CHEVRON_WIDTH, flex: "none" }} />
      )}
      <Tooltip title={t("openSearch")}>
        <Link
          component="button"
          variant="button"
          underline="hover"
          tabIndex={tabIndex}
          onClick={() => {
            navigate(session.last_search_id);
          }}
          sx={{ textAlign: "left", py: 0.5 }}
        >
          <HighlightedText text={session.name} query={highlight} />
        </Link>
      </Tooltip>
      {canOpen ? (
        <Chip
          size="small"
          sx={{ ml: "auto", flex: "none" }}
          label={
            listedVersionCount < versionCount
              ? t("matchingOfTotal", {
                  matching: listedVersionCount,
                  total: versionCount,
                })
              : versionCount
          }
        />
      ) : null}
    </Box>
  );
};

const SessionOptionsButtonCell = ({
  row,
}: GridRenderCellParams<SearchesGridRow>) =>
  row.kind === "session" ? (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        width: "100%",
        height: "100%",
      }}
    >
      <SessionOptionsButton session={row.session} />
    </Box>
  ) : null;
